/* XNote Design v2 motion engine (docs/design-v2.md §5; API: docs/build/foundation.md §16.4). Deferred, no
   dependencies, loaded by layout/xp.liquid after xp-base.js. One IntersectionObserver pool per threshold; one passive
   scroll listener + requestAnimationFrame, running only while a registered track is near the viewport; rects cached
   and refreshed by ResizeObserver (no layout reads in the frame loop). Reduced motion, Save-Data and hidden tabs are
   respected everywhere; nothing here can hide content: without this file every element renders in its final state. */
(() => {
  'use strict';
  const d = document, w = window, root = d.documentElement;
  const XP = (w.XP = w.XP || {});
  const $$ = (s, el = d) => Array.from(el.querySelectorAll(s));
  const mq = (q) => w.matchMedia(q);
  const rmq = mq('(prefers-reduced-motion: reduce)');
  const fine = mq('(hover: hover) and (pointer: fine)');
  const phone = mq('(max-width: 767px)');
  const wide = mq('(min-width: 1024px)');
  const conn = navigator.connection || {};
  const lite = root.classList.contains('xp-lite') || Boolean(conn.saveData) || /2g$/.test(conn.effectiveType || '');
  const hasIO = 'IntersectionObserver' in w;
  let reduced = rmq.matches;
  let loaded = d.readyState === 'complete';
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const emit = (el, name, detail) => el.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  const afterLoad = (fn) => (loaded ? fn() : w.addEventListener('load', fn, { once: true }));
  w.addEventListener('load', () => { loaded = true; }, { once: true });
  const flags = () => { root.classList.toggle('xp-still', reduced); root.classList.toggle('xp-motion', !reduced); };

  /* ---------- IntersectionObserver pool: watch(el, fn, {threshold, margin, once}) ---------- */
  const pools = new Map();
  function watch(el, fn, o = {}) {
    const once = o.once !== false;
    if (!hasIO) { fn({ isIntersecting: true, target: el }); return; }
    const key = (o.threshold || 0) + '|' + (o.margin || '0px');
    let p = pools.get(key);
    if (!p) {
      const map = new Map();
      p = { map, io: new IntersectionObserver((es) => es.forEach((e) => {
        (map.get(e.target) || []).slice().forEach((h) => {
          if (h.once && !e.isIntersecting) return;
          h.fn(e);
          if (h.once) { const l = map.get(e.target).filter((x) => x !== h); if (l.length) map.set(e.target, l); else { map.delete(e.target); p.io.unobserve(e.target); } }
        });
      }), { threshold: o.threshold || 0, rootMargin: o.margin || '0px' }) };
      pools.set(key, p);
    }
    const list = p.map.get(el) || [];
    list.push({ fn, once });
    p.map.set(el, list);
    p.io.observe(el);
  }

  /* ---------- Scroll engine: register(el, fn(track, scrollY), measure?) ---------- */
  const tracks = new Map();
  const live = new Set();
  let vh = w.innerHeight, queued = false, trackIO = null;
  const measure = (el, t) => { const r = el.getBoundingClientRect(); t.top = r.top + w.scrollY; t.h = r.height; if (t.m) t.m(t); };
  const tick = () => { queued = false; const y = w.scrollY; live.forEach((el) => { const t = tracks.get(el); if (t) t.fn(t, y); }); };
  const request = () => { if (!queued && live.size) { queued = true; w.requestAnimationFrame(tick); } };
  const remeasure = () => { vh = w.innerHeight; tracks.forEach((t, el) => measure(el, t)); request(); };
  const ro = 'ResizeObserver' in w ? new ResizeObserver(() => remeasure()) : null;
  function register(el, fn, m) {
    if (!hasIO || tracks.has(el)) return;
    const t = { fn, m, top: 0, h: 0, el };
    tracks.set(el, t);
    measure(el, t);
    trackIO = trackIO || new IntersectionObserver((es) => { es.forEach((e) => (e.isIntersecting ? live.add(e.target) : live.delete(e.target))); request(); }, { rootMargin: '25% 0px' });
    trackIO.observe(el);
    if (ro) ro.observe(el);
  }
  const progress = (t, y) => clamp((y - t.top) / Math.max(1, t.h - vh)); // sticky track: 0 at its top, 1 at its end
  w.addEventListener('scroll', request, { passive: true });
  w.addEventListener('resize', remeasure);
  if (ro) ro.observe(d.body);

  /* ---------- Reveal, kinetic statements, stroke draw (once, at 15 %) ---------- */
  function initReveal(scope) {
    $$('[data-reveal-stagger]', scope).forEach((p) => Array.from(p.children).forEach((c, i) => {
      if (!c.style.getPropertyValue('--i')) c.style.setProperty('--i', i);
      if (!c.hasAttribute('data-reveal')) c.setAttribute('data-reveal', p.getAttribute('data-reveal-stagger') || '');
    }));
    $$('[data-reveal], [data-kinetic="reveal"], [data-draw], .xp-paper__sheet', scope).forEach((el) => watch(el, () => el.classList.add('is-in'), { threshold: 0.15 }));
  }

  /* ---------- Numbers render static (docs/design-v3-light.md §1.5, §1.8): no count-up; xp-stat ignores count_to ---------- */

  /* ---------- Offscreen pause: marquees run only while visible ---------- */
  function initPausers(scope) {
    $$('[data-marquee]', scope).forEach((el) => watch(el, (e) => el.classList.toggle('is-off', !e.isIntersecting), { once: false }));
  }

  /* ---------- Hover tilt (fine pointers only, never with reduced motion). The pointer-following light and the
     magnetic pull are retired (§1.8 kill list). ---------- */
  function initPointer(scope) {
    if (!fine.matches) return;
    $$('[data-tilt]', scope).forEach((el) => {
      let raf = 0, ev = null;
      const apply = () => {
        raf = 0;
        const r = el.getBoundingClientRect(), px = (ev.clientX - r.left) / r.width, py = (ev.clientY - r.top) / r.height;
        el.style.setProperty('--rx', ((0.5 - py) * 8).toFixed(2) + 'deg'); el.style.setProperty('--ry', ((px - 0.5) * 8).toFixed(2) + 'deg');
      };
      el.addEventListener('pointermove', (e) => { if (reduced || e.pointerType !== 'mouse') return; ev = e; el.classList.add('is-tilting'); if (!raf) raf = w.requestAnimationFrame(apply); });
      el.addEventListener('pointerleave', () => { el.classList.remove('is-tilting'); el.style.setProperty('--rx', '0deg'); el.style.setProperty('--ry', '0deg'); });
    });
  }

  /* ---------- Parallax depth (desktop ≥ 1024 px): --p = -1…1 as the element crosses the viewport ---------- */
  function initDepth(scope) {
    if (reduced || !wide.matches) return;
    $$('[data-depth]', scope).forEach((el) => {
      el.style.setProperty('--depth', el.dataset.depth || '0.2');
      register(el, (t, y) => el.style.setProperty('--p', clamp((t.top + t.h / 2 - y - vh / 2) / (vh / 2 + t.h / 2), -1, 1).toFixed(3)));
    });
  }

  /* ---------- Videos: helpers shared by scrolly, crossfades and the pause toggles ---------- */
  const canPlay = () => !reduced && !lite && !d.hidden;
  const poster = (v) => { if (v && v.dataset.xpPoster) { v.poster = v.dataset.xpPoster; delete v.dataset.xpPoster; } };
  const play = (v) => {
    if (!v || v._xpUserPaused || !canPlay()) return;
    poster(v); v.muted = true;
    if (v.preload === 'none') v.preload = 'auto';
    const p = v.play(); if (p && p.catch) p.catch(() => {});
  };
  function initVideoBits(scope) {
    // Poster → video crossfade (no pop): the wrapper gets .is-live once the clip is actually playing.
    $$('[data-xfade]', scope).forEach((box) => {
      const v = box.querySelector('video');
      if (!v) return;
      v.addEventListener('playing', () => box.classList.add('is-live'));
      if (!box.closest('[data-scrolly]') && XP.observeVideo) XP.observeVideo(v);
    });
    // Pause toggles for motion-owned loops (scrolly beats): focus-only button, a click on the clip toggles too.
    $$('[data-xp-mpause]', scope).forEach((b) => {
      const v = b.parentElement && b.parentElement.querySelector('video');
      if (!v) return;
      const toggle = () => { v._xpUserPaused = !v._xpUserPaused; b.setAttribute('aria-pressed', String(v._xpUserPaused)); if (v._xpUserPaused) v.pause(); else if (v._xpActive !== false) play(v); };
      b.hidden = !canPlay();
      b.addEventListener('click', toggle);
      v.addEventListener('click', toggle);
    });
  }

  /* ---------- Sticky scrollytelling: [data-scrolly] > [data-step]… + [data-beat]… (media) ----------
     Desktop (and motion allowed): the media column is sticky; the step under the viewport's centre line is active;
     a step can own several beats (data-step-media="2,3") that advance with its local progress. Only the active beat's
     video plays; the active and next beats' posters are primed only after `load` and once the track is within one
     viewport (integration pass: they were fetched at scan time, screens early). Phones, reduced motion and Save-Data:
     stacked steps, media inline, loops play in view through xp-base.js (poster only when motion is reduced or data is
     saved). */
  function initScrolly(scope) {
    $$('[data-scrolly]', scope).forEach((el) => {
      const steps = $$('[data-step]', el), media = $$('[data-beat]', el), rail = $$('[data-rail-item]', el);
      const vids = media.map((m) => m.querySelector('video'));
      if (phone.matches || reduced || lite || !hasIO) { vids.forEach((v) => v && XP.observeVideo && XP.observeVideo(v)); return; }
      el.classList.add('is-live');
      const map = steps.map((s, i) => (s.dataset.stepMedia || String(Math.min(i, media.length - 1))).split(',').map(Number));
      let step = -1, beat = -1, onScreen = false, near = false;
      const show = (si, bi) => {
        if (si !== step) { step = si; steps.forEach((s, k) => s.classList.toggle('is-active', k === si)); }
        if (bi === beat) return;
        beat = bi;
        el.style.setProperty('--beat', bi);
        media.forEach((m, k) => m.classList.toggle('is-active', k === bi));
        rail.forEach((r, k) => { r.classList.toggle('is-active', k === bi); r.classList.toggle('is-done', k < bi); });
        vids.forEach((v, k) => { if (!v) return; v._xpActive = k === bi; if (k === bi) afterLoad(() => { if (v._xpActive && onScreen) play(v); }); else { if (!v.paused) v.pause(); if (k === bi + 1 && near) poster(v); } });
        emit(el, 'xp:step', { track: el, index: si, beat: bi });
      };
      register(el, (t, y) => {
        const line = y + vh * 0.5;
        let si = 0;
        steps.forEach((s, i) => { if (s._top <= line) si = i; });
        const s = steps[si], beats = map[si];
        const local = clamp((line - s._top) / Math.max(1, s._h));
        el.style.setProperty('--p', progress(t, y).toFixed(4));
        show(si, beats[Math.min(beats.length - 1, Math.floor(local * beats.length))]);
      }, () => steps.forEach((s) => { const r = s.getBoundingClientRect(); s._top = r.top + w.scrollY; s._h = r.height; }));
      show(0, map[0] ? map[0][0] : 0);
      watch(el, () => afterLoad(() => { near = true; poster(vids[Math.max(0, beat)]); poster(vids[beat + 1]); }), { margin: '100% 0px' });
      // Only while the track is on screen and the tab is visible does the active beat's loop run.
      const sync = (on) => vids.forEach((v, k) => { if (!v) return; if (on && k === beat) afterLoad(() => { if (v._xpActive && !d.hidden) play(v); }); else if (!v.paused) v.pause(); });
      watch(el, (e) => { onScreen = e.isIntersecting; sync(onScreen); }, { once: false });
      d.addEventListener('visibilitychange', () => sync(onScreen && !d.hidden));
    });
  }

  /* ---------- Frame-sequence scrub (canvas): [data-scrub] ----------
     data-src = the first frame's URL (…-001.webp); data-frames = count; data-stride (file step, default 1).
     Phones use data-src-m / data-frames-m / data-stride-m when given. Frames load only after `load` and once the
     track is within one viewport, coarse to fine (0, ½, ¼, ¾, …), at most 6 in flight; the nearest loaded frame is
     drawn, so the canvas is never blank. Reduced motion, Save-Data or no frames: the still <img> stays.
     data-scrub-view: no sticky track; progress follows the element through the viewport instead.
     data-scrub-margin (integration pass): the rootMargin that arms the frame loading (default '100% 0px' = within one
     viewport); e.g. '-25%' arms a sequence next to the fold only once it is well inside the viewport, so pages need not
     park it with data-ready="later" and call scan(). */
  function initScrub(scope) {
    $$('.xp-seq__video', scope).forEach((v) => { if (phone.matches && XP.observeVideo) XP.observeVideo(v); });
    $$('[data-scrub]', scope).forEach((el) => {
      if (phone.matches && el.querySelector('.xp-seq__video')) return; // phones play the loop instead of frames
      const canvas = el.querySelector('canvas');
      const small = phone.matches && el.dataset.srcM;
      const src = small ? el.dataset.srcM : el.dataset.src;
      const n = Number(small ? el.dataset.framesM : el.dataset.frames) || 0;
      const stride = Number((small ? el.dataset.strideM : el.dataset.stride) || 1);
      const m = src && src.match(/^(.*?)(\d+)(\.\w+(?:\?.*)?)$/);
      if (!canvas || !m || n < 2 || reduced || lite || !hasIO || (el.dataset.ready !== 'true' && !w.XP_FORCE_FRAMES)) return;
      const first = Number(m[2]), digits = m[2].length;
      const url = (i) => m[1] + String(first + i * stride).padStart(digits, '0') + m[3];
      const imgs = new Array(n), ctx = canvas.getContext('2d');
      const chips = $$('[data-at]', el);
      let want = 0, cur = -1, started = false;
      el.classList.add('is-scrub');
      const nearest = (i) => { for (let k = 0; k < n; k++) { if (imgs[i + k]) return i + k; if (imgs[i - k]) return i - k; } return -1; };
      const draw = (i) => {
        want = i;
        const j = nearest(i);
        if (j < 0 || j === cur || !canvas.width) return;
        cur = j;
        const im = imgs[j], cw = canvas.width, ch = canvas.height, s = Math.min(cw / im.naturalWidth, ch / im.naturalHeight);
        const dw = im.naturalWidth * s, dh = im.naturalHeight * s;
        ctx.clearRect(0, 0, cw, ch);
        ctx.drawImage(im, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
        el.classList.add('is-live');
      };
      const size = () => {
        const r = canvas.getBoundingClientRect(), dpr = Math.min(w.devicePixelRatio || 1, 2);
        canvas.width = Math.round(r.width * dpr); canvas.height = Math.round(r.height * dpr); cur = -1; draw(want);
      };
      const order = [];
      for (let div = 1; div <= n; div *= 2) for (let k = 0; k < div; k++) { const i = Math.floor(((2 * k + 1) * n) / (2 * div)) % n; if (!order.includes(i)) order.push(i); }
      if (!order.includes(0)) order.unshift(0);
      for (let i = 0; i < n; i++) if (!order.includes(i)) order.push(i);
      order.splice(order.indexOf(0), 1); order.unshift(0);
      const start = () => {
        if (started) return;
        started = true;
        size();
        if (ro) new ResizeObserver(size).observe(canvas);
        let inflight = 0, q = 0;
        const next = () => {
          while (inflight < 6 && q < order.length) {
            const i = order[q++], im = new Image();
            inflight++;
            im.decoding = 'async';
            im.src = url(i);
            (im.decode ? im.decode() : Promise.resolve()).then(() => { imgs[i] = im; if (cur < 0 || Math.abs(i - want) < Math.abs(cur - want)) { cur = -1; draw(want); } })
              .catch(() => {}).then(() => { inflight--; next(); });
          }
        };
        next();
      };
      watch(el, () => afterLoad(start), { margin: el.dataset.scrubMargin || '100% 0px' });
      // data-scrub-view (no sticky track, e.g. the price moment's turntable): progress = the element crossing the
      // viewport, 0 when its top enters at the bottom, 1 when its bottom leaves at the top.
      const view = el.hasAttribute('data-scrub-view');
      register(el, (t, y) => {
        const p = view ? clamp((y + vh - t.top) / Math.max(1, vh + t.h)) : progress(t, y);
        el.style.setProperty('--p', p.toFixed(4));
        el.style.setProperty('--gx', (Math.sin(p * Math.PI * 2) * 40).toFixed(1) + 'px');
        chips.forEach((c) => c.classList.toggle('is-on', p >= Number(c.dataset.at)));
        draw(Math.min(n - 1, Math.floor(p * n)));
        emit(el, 'xp:scrub', { track: el, p });
      });
    });
  }

  /* ---------- Handwriting demo (xp-handwrite-demo): plays once in view, pauses offscreen, Replay ---------- */
  function initHandwrite(scope) {
    $$('[data-xp-handwrite]', scope).forEach((el) => {
      const rows = $$('.xp-hw__row', el), lines = $$('.xp-hw__line', el), pen = el.querySelector('.xp-hw__pen');
      const ink = el.querySelector('.xp-hw__ink'), txt = el.querySelector('.xp-hw__text'), card = el.querySelector('.xp-hw__card');
      const marks = $$('.xp-hw__mark', el), replay = el.querySelector('[data-replay]');
      if (!ink || !txt || !card || typeof el.animate !== 'function') return;
      let anims = [];
      const opts = (delay, duration, easing = 'cubic-bezier(.16,1,.3,1)') => ({ delay, duration, easing, fill: 'both' });
      const build = () => {
        anims.forEach((a) => a.cancel());
        anims = [];
        el.classList.add('is-playing');
        const per = 330, gap = 40, rowsEnd = rows.length * (per + gap);
        const pk = [];
        rows.forEach((r, i) => {
          // A row's clip box is its own ink bounding box (SVG fill-box); the pen follows the row's baseline on the page.
          const cs = getComputedStyle(r), v = (k) => parseFloat(cs.getPropertyValue(k)) * 100;
          const top = v('--y0') + (v('--y1') - v('--y0')) * 0.72 + '%';
          anims.push(r.animate([{ clipPath: 'inset(-20% 100% -20% 0)' }, { clipPath: 'inset(-20% -2% -20% 0)' }], opts(i * (per + gap), per, 'linear')));
          pk.push({ offset: (i * (per + gap)) / rowsEnd, left: v('--x0') + '%', top, opacity: 1 }, { offset: (i * (per + gap) + per) / rowsEnd, left: v('--x1') + '%', top, opacity: 1 });
        });
        if (pen) { pk.push({ offset: 1, left: pk[pk.length - 1].left, top: pk[pk.length - 1].top, opacity: 0 }); anims.push(pen.animate(pk, { duration: rowsEnd, fill: 'both' })); }
        const t1 = rowsEnd + 250;
        anims.push(ink.animate([{ opacity: 1 }, { opacity: 0 }], opts(t1, 600, 'ease')));
        lines.forEach((l, i) => anims.push(l.animate([{ opacity: 0, transform: 'translateY(6px)' }, { opacity: 1, transform: 'none' }], opts(t1 + 150 + i * 70, 450))));
        const t2 = t1 + 150 + lines.length * 70 + 500;
        anims.push(card.animate([{ opacity: 0, transform: 'translateY(40px) scale(.98)' }, { opacity: 1, transform: 'none' }], opts(t2, 700)));
        marks.forEach((k) => anims.push(k.animate([{ backgroundSize: '0% 72%' }, { backgroundSize: '100% 72%' }], opts(t2 + 800, 700))));
        anims[anims.length - 1].finished.then(() => { el.classList.add('is-done'); if (replay) replay.hidden = false; }).catch(() => {});
      };
      const finish = () => { el.classList.add('is-final'); anims.forEach((a) => a.finish()); };
      if (reduced) { el.classList.add('is-final'); return; }
      el.classList.add('is-armed');
      watch(el, () => { build(); watch(el, (e) => anims.forEach((a) => (e.isIntersecting && !d.hidden ? a.play() : a.pause())), { once: false, threshold: 0 }); }, { threshold: 0.35 });
      if (replay) replay.addEventListener('click', () => { el.classList.remove('is-done', 'is-final'); build(); });
      rmq.addEventListener('change', () => { if (rmq.matches) finish(); });
    });
  }

  /* ---------- Drag to scroll (mouse) for snap rows: [data-drag-scroll] ---------- */
  function initDrag(scope) {
    $$('[data-drag-scroll]', scope).forEach((el) => {
      let x0 = 0, s0 = 0, down = false, moved = false;
      el.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse' || e.button) return; down = true; moved = false; x0 = e.clientX; s0 = el.scrollLeft; });
      w.addEventListener('pointermove', (e) => { if (!down) return; const dx = e.clientX - x0; if (Math.abs(dx) > 4) { moved = true; el.classList.add('is-drag'); } if (moved) el.scrollLeft = s0 - dx; });
      w.addEventListener('pointerup', () => { if (down) { down = false; el.classList.remove('is-drag'); } });
      el.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
    });
  }

  /* ---------- Init ---------- */
  function scan(scope = d) {
    initReveal(scope); initPausers(scope); initDepth(scope); initVideoBits(scope);
    initScrolly(scope); initScrub(scope); initHandwrite(scope); initDrag(scope);
    if (!reduced) initPointer(scope);
  }
  flags();
  rmq.addEventListener('change', () => {
    reduced = rmq.matches;
    flags();
    if (reduced) { $$('video').forEach((v) => { if (!v.paused && !v.controls) v.pause(); }); $$('.xp-count.is-counting').forEach((c) => c.classList.remove('is-counting')); }
  });
  d.addEventListener('visibilitychange', () => root.classList.toggle('xp-tab-hidden', d.hidden));
  XP.motion = { reduced, lite, register, watch, scan, refresh: remeasure };
  scan();
})();
